import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Badge, Button, ButtonGroup, Card, Col, Container, Form,
  InputGroup, Modal, Row, Spinner, Table
} from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { formatDateTH } from '../utils/dateUtils'

const fmt = (n) =>
  Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function BankAccountDetail() {
  const { id }     = useParams()
  const navigate   = useNavigate()

  const [account,      setAccount]      = useState(null)
  const [transactions, setTransactions] = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')

  const [typeFilter,     setTypeFilter]     = useState('')
  const [fromDate,       setFromDate]       = useState(null)
  const [toDate,         setToDate]         = useState(null)
  const [txLoading,      setTxLoading]      = useState(false)
  const [exporting,      setExporting]      = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [closeLoading,   setCloseLoading]   = useState(false)
  const [closeStudentId, setCloseStudentId] = useState('')
  const [closeVerifyError, setCloseVerifyError] = useState('')

  const fetchAccount = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/bank/accounts/${id}`)
      if (res.data.success) setAccount(res.data.data)
      else setError('ไม่พบข้อมูลบัญชี')
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลบัญชีไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [id])

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true)
    try {
      const params = new URLSearchParams({ account_id: id })
      if (typeFilter) params.set('transaction_type', typeFilter)
      if (fromDate)   params.set('from_date', fromDate.toISOString().slice(0, 10))
      if (toDate)     params.set('to_date',   toDate.toISOString().slice(0, 10))
      const res = await axiosInstance.get(`/api/bank/transactions?${params}`)
      if (res.data.success) setTransactions(res.data.data)
    } catch (_) {
      setTransactions([])
    } finally {
      setTxLoading(false)
    }
  }, [id, typeFilter, fromDate, toDate])

  useEffect(() => { fetchAccount() }, [fetchAccount])
  useEffect(() => { if (!loading) fetchTransactions() }, [fetchTransactions, loading])

  const totalDeposit    = transactions.filter(t => t.transaction_type === 'deposit').reduce((s, t) => s + parseFloat(t.amount), 0)
  const totalWithdrawal = transactions.filter(t => t.transaction_type === 'withdrawal').reduce((s, t) => s + parseFloat(t.amount), 0)

  const buildStatementHTML = () => {
    const rows = transactions.map((t, i) => `
      <tr>
        <td style="border:1px solid #dee2e6;padding:5px 8px;text-align:center;">${i + 1}</td>
        <td style="border:1px solid #dee2e6;padding:5px 8px;">${formatDateTH(t.occurred_date?.slice(0, 10))}</td>
        <td style="border:1px solid #dee2e6;padding:5px 8px;text-align:center;">${t.transaction_type === 'deposit' ? 'ฝาก' : 'ถอน'}</td>
        <td style="border:1px solid #dee2e6;padding:5px 8px;text-align:right;color:${t.transaction_type === 'deposit' ? '#198754' : '#dc3545'};">
          ${t.transaction_type === 'deposit' ? '+' : '-'}${fmt(t.amount)}
        </td>
        <td style="border:1px solid #dee2e6;padding:5px 8px;text-align:right;">${fmt(t.balance_after)}</td>
        <td style="border:1px solid #dee2e6;padding:5px 8px;">${t.note || '-'}</td>
      </tr>`).join('')

    const printedAt = new Date().toLocaleString('th-TH')
    const filterLabel = [
      typeFilter === 'deposit' ? 'ฝากเท่านั้น' : typeFilter === 'withdrawal' ? 'ถอนเท่านั้น' : '',
      fromDate ? `ตั้งแต่ ${formatDateTH(fromDate)}` : '',
      toDate   ? `ถึง ${formatDateTH(toDate)}`       : '',
    ].filter(Boolean).join(' · ') || 'ทุกรายการ'

    return `
      <div style="font-family:'Sarabun',Arial,sans-serif;font-size:13px;color:#212529;padding:24px;max-width:800px;margin:0 auto;">
        <div style="text-align:center;margin-bottom:16px;">
          <div style="font-size:18px;font-weight:700;">ใบรายเดินบัญชี</div>
          <div style="font-size:13px;color:#6c757d;">ธนาคารนักเรียน โรงเรียนวัฒนธรรมอิสลาม</div>
        </div>
        <hr style="border-color:#dee2e6;margin:12px 0;" />
        <table style="width:100%;margin-bottom:12px;">
          <tr>
            <td style="width:50%;vertical-align:top;">
              <div><b>ชื่อเจ้าของบัญชี:</b> ${account.st_name}</div>
              <div><b>รหัสนักเรียน:</b> ${account.st_idnumber}</div>
              <div><b>เลขบัญชี:</b> ${account.account_no}</div>
            </td>
            <td style="width:50%;vertical-align:top;text-align:right;">
              <div><b>ปีการศึกษา:</b> ${account.study_year} เล่ม ${account.book_no}</div>
              <div><b>วันที่เปิดบัญชี:</b> ${formatDateTH(account.open_date?.slice(0, 10))}</div>
              <div><b>สถานะ:</b> ${account.account_status === 'active' ? 'เปิดอยู่' : 'ปิดแล้ว'}</div>
            </td>
          </tr>
        </table>
        <div style="margin-bottom:8px;font-size:12px;color:#6c757d;">ตัวกรอง: ${filterLabel}</div>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:#f8f9fa;">
              <th style="border:1px solid #dee2e6;padding:6px 8px;text-align:center;">#</th>
              <th style="border:1px solid #dee2e6;padding:6px 8px;">วันที่</th>
              <th style="border:1px solid #dee2e6;padding:6px 8px;text-align:center;">ประเภท</th>
              <th style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;">จำนวนเงิน</th>
              <th style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;">ยอดคงเหลือ</th>
              <th style="border:1px solid #dee2e6;padding:6px 8px;">หมายเหตุ</th>
            </tr>
          </thead>
          <tbody>
            ${rows || '<tr><td colspan="6" style="border:1px solid #dee2e6;padding:8px;text-align:center;">ไม่มีรายการ</td></tr>'}
          </tbody>
          <tfoot>
            <tr style="background:#f8f9fa;font-weight:700;">
              <td colspan="3" style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;">รวม</td>
              <td style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;color:#198754;">+${fmt(totalDeposit)}</td>
              <td colspan="2" style="border:1px solid #dee2e6;padding:6px 8px;"></td>
            </tr>
            <tr style="background:#f8f9fa;font-weight:700;">
              <td colspan="3" style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;"></td>
              <td style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;color:#dc3545;">-${fmt(totalWithdrawal)}</td>
              <td colspan="2" style="border:1px solid #dee2e6;padding:6px 8px;"></td>
            </tr>
            <tr style="background:#e9ecef;font-weight:700;">
              <td colspan="4" style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;">ยอดคงเหลือปัจจุบัน</td>
              <td style="border:1px solid #dee2e6;padding:6px 8px;text-align:right;color:#0d6efd;">฿${fmt(account.current_balance)}</td>
              <td style="border:1px solid #dee2e6;padding:6px 8px;"></td>
            </tr>
          </tfoot>
        </table>
        <div style="margin-top:12px;font-size:11px;color:#6c757d;text-align:right;">พิมพ์เมื่อ: ${printedAt} · จำนวน ${transactions.length} รายการ</div>
      </div>`
  }

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=900,height=700')
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8" />
      <title>รายเดินบัญชี ${account.account_no}</title>
      <link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@400;700&display=swap" rel="stylesheet">
      <style>body{margin:0;} @media print{body{margin:0;}}</style>
    </head><body>${buildStatementHTML()}</body></html>`)
    win.document.close()
    win.onload = () => { win.focus(); win.print() }
  }

  const handleCloseAccount = async () => {
    if (!account) return
    if (closeStudentId.trim() !== String(account.st_idnumber).trim()) {
      setCloseVerifyError('เลขประจำตัวไม่ตรงกับบัญชีนี้')
      return
    }

    setCloseLoading(true)
    setCloseVerifyError('')
    setError('')
    try {
      await axiosInstance.patch(`/api/bank/accounts/${id}/close`)
      setShowCloseModal(false)
      setCloseStudentId('')
      await fetchAccount()
      await fetchTransactions()
    } catch (err) {
      setError(err.response?.data?.message || 'ปิดบัญชีไม่สำเร็จ')
    } finally {
      setCloseLoading(false)
    }
  }

  const handleExportPDF = async () => {
    setExporting(true)
    try {
      const el = document.createElement('div')
      el.style.cssText = 'position:absolute;left:-9999px;top:0;background:#fff;'
      el.innerHTML = buildStatementHTML()
      document.body.appendChild(el)

      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
      document.body.removeChild(el)

      const imgData   = canvas.toDataURL('image/png')
      const doc       = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const pageW     = doc.internal.pageSize.getWidth()
      const pageH     = doc.internal.pageSize.getHeight()
      const imgW      = pageW - 40
      const imgH      = (canvas.height * imgW) / canvas.width
      const margin    = 20

      if (imgH <= pageH - margin * 2) {
        doc.addImage(imgData, 'PNG', margin, margin, imgW, imgH)
      } else {
        let heightLeft = imgH
        let position   = margin
        doc.addImage(imgData, 'PNG', margin, position, imgW, imgH)
        heightLeft -= (pageH - margin * 2)
        while (heightLeft > 0) {
          doc.addPage()
          position = margin - (imgH - heightLeft)
          doc.addImage(imgData, 'PNG', margin, position, imgW, imgH)
          heightLeft -= (pageH - margin * 2)
        }
      }

      doc.save(`statement-${account.account_no}-${new Date().toISOString().slice(0, 10)}.pdf`)
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4 text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          {/* Title */}
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <Button variant="outline-secondary" size="sm" onClick={() => navigate('/bank/accounts')}>
                <i className="ri-arrow-left-line" />
              </Button>
              <h4 className="main-title mb-0">
                <i className="ri-bank-card-line me-2 text-primary" />
                รายละเอียดบัญชี
              </h4>
            </div>
            {account && (
              <ButtonGroup size="sm">
                <Button variant="outline-primary" onClick={handlePrint} disabled={exporting}>
                  <i className="ri-printer-line me-1" />ปริ้น
                </Button>
                <Button variant="outline-danger" onClick={handleExportPDF} disabled={exporting}>
                  {exporting
                    ? <><Spinner size="sm" className="me-1" />กำลังสร้าง PDF...</>
                    : <><i className="ri-file-pdf-line me-1" />ส่งออก PDF</>
                  }
                </Button>
                {account.account_status === 'active' && (
                  <Button variant="danger" onClick={() => setShowCloseModal(true)} disabled={closeLoading}>
                    <i className="ri-close-circle-line me-1" />ปิดบัญชี
                  </Button>
                )}
              </ButtonGroup>
            )}
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {account && (
            <>
              {/* Account info */}
              <Row className="g-3 mb-4">
                <Col md={8}>
                  <Card className="card-one">
                    <Card.Body className="p-3">
                      <Row className="g-2">
                        <Col xs={6} md={4}>
                          <div className="fs-sm text-muted">เลขบัญชี</div>
                          <div className="fw-bold fs-5"><code>{account.account_no}</code></div>
                        </Col>
                        <Col xs={6} md={4}>
                          <div className="fs-sm text-muted">ปีการศึกษา</div>
                          <div className="fw-semibold">{account.study_year} (เล่ม {account.book_no})</div>
                        </Col>
                        <Col xs={6} md={4}>
                          <div className="fs-sm text-muted">วันที่เปิดบัญชี</div>
                          <div>{formatDateTH(account.open_date?.slice(0, 10))}</div>
                        </Col>
                        <Col xs={6} md={4}>
                          <div className="fs-sm text-muted">ชื่อนักเรียน</div>
                          <div className="fw-semibold">{account.st_name}</div>
                        </Col>
                        <Col xs={6} md={4}>
                          <div className="fs-sm text-muted">รหัสนักเรียน</div>
                          <div>{account.st_idnumber}</div>
                        </Col>
                        <Col xs={6} md={4}>
                          <div className="fs-sm text-muted">สถานะ</div>
                          <Badge bg={account.account_status === 'active' ? 'success' : 'secondary'}>
                            {account.account_status === 'active' ? 'เปิดอยู่' : 'ปิดแล้ว'}
                          </Badge>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="card-one h-100 text-center">
                    <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                      <div className="fs-sm text-muted mb-1">ยอดคงเหลือปัจจุบัน</div>
                      <h3 className="text-primary mb-0">฿{fmt(account.current_balance)}</h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Summary row */}
              <Row className="g-3 mb-3">
                <Col xs={4}>
                  <Card className="card-one text-center">
                    <Card.Body className="p-2">
                      <div className="fs-sm text-muted">ฝากรวม (กรอง)</div>
                      <div className="fw-bold text-success">฿{fmt(totalDeposit)}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={4}>
                  <Card className="card-one text-center">
                    <Card.Body className="p-2">
                      <div className="fs-sm text-muted">ถอนรวม (กรอง)</div>
                      <div className="fw-bold text-danger">฿{fmt(totalWithdrawal)}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={4}>
                  <Card className="card-one text-center">
                    <Card.Body className="p-2">
                      <div className="fs-sm text-muted">จำนวนรายการ</div>
                      <div className="fw-bold">{transactions.length}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Filter bar */}
              <Card className="card-one mb-3">
                <Card.Body className="p-2">
                  <Row className="g-2 align-items-end">
                    <Col xs={6} md={3}>
                      <Form.Label className="fs-sm mb-1">ประเภท</Form.Label>
                      <Form.Select size="sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                        <option value="">ทั้งหมด</option>
                        <option value="deposit">ฝาก</option>
                        <option value="withdrawal">ถอน</option>
                      </Form.Select>
                    </Col>
                    <Col xs={6} md={3}>
                      <Form.Label className="fs-sm mb-1">จากวันที่</Form.Label>
                      <DatePicker
                        selected={fromDate}
                        onChange={setFromDate}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="เลือกวันที่"
                        className="form-control form-control-sm"
                        isClearable
                      />
                    </Col>
                    <Col xs={6} md={3}>
                      <Form.Label className="fs-sm mb-1">ถึงวันที่</Form.Label>
                      <DatePicker
                        selected={toDate}
                        onChange={setToDate}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="เลือกวันที่"
                        className="form-control form-control-sm"
                        isClearable
                      />
                    </Col>
                    <Col xs={6} md={3}>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => { setTypeFilter(''); setFromDate(null); setToDate(null) }}
                      >
                        <i className="ri-refresh-line me-1" />ล้าง
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Transactions table */}
              <Card className="card-one">
                <Card.Body className="p-0">
                  {txLoading ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : (
                    <Table responsive hover className="mb-0 fs-sm">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>วันที่เกิดรายการ</th>
                          <th>ประเภท</th>
                          <th className="text-end">จำนวน</th>
                          <th className="text-end">ยอดคงเหลือ</th>
                          <th>หมายเหตุ</th>
                          <th>บันทึกเมื่อ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.length === 0 ? (
                          <tr><td colSpan={7} className="text-center text-muted py-4">ไม่มีรายการ</td></tr>
                        ) : transactions.map((t, i) => (
                          <tr key={t.transaction_id}>
                            <td className="text-muted">{i + 1}</td>
                            <td>{formatDateTH(t.occurred_date?.slice(0, 10))}</td>
                            <td>
                              <Badge bg={t.transaction_type === 'deposit' ? 'success' : 'danger'}>
                                {t.transaction_type === 'deposit' ? 'ฝาก' : 'ถอน'}
                              </Badge>
                            </td>
                            <td className={`text-end fw-semibold ${t.transaction_type === 'deposit' ? 'text-success' : 'text-danger'}`}>
                              {t.transaction_type === 'deposit' ? '+' : '-'}฿{fmt(t.amount)}
                            </td>
                            <td className="text-end">฿{fmt(t.balance_after)}</td>
                            <td>{t.note || <span className="text-muted">-</span>}</td>
                            <td className="text-muted" style={{ fontSize: '11px' }}>
                              {t.created_at ? new Date(t.created_at).toLocaleString('th-TH') : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>

      <Modal show={showCloseModal} onHide={() => { setShowCloseModal(false); setCloseStudentId(''); setCloseVerifyError('') }} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">ยืนยันปิดบัญชี</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>คุณต้องการปิดบัญชีนี้ใช่หรือไม่? หากมียอดคงเหลือ ระบบจะถอนออกอัตโนมัติและปิดบัญชี</div>
          {account && (
            <>
              <div className="mt-3 border-top pt-3">
                <div className="text-muted fs-sm">ชื่อนักเรียน</div>
                <div className="fw-semibold">{account.st_name}</div>
                <div className="text-muted fs-sm mt-2">เลขประจำตัวนักเรียน</div>
                <div className="fw-bold">{account.st_idnumber}</div>
              </div>
              <Form.Group className="mt-3">
                <Form.Label className="fs-sm mb-1">พิมพ์เลข {account.st_idnumber} เพื่อยืนยัน</Form.Label>
                <Form.Control
                  size="sm"
                  value={closeStudentId}
                  onChange={e => {
                    setCloseStudentId(e.target.value)
                    setCloseVerifyError('')
                  }}
                  placeholder="ระบุเลขประจำตัวนักเรียน"
                  autoComplete="off"
                />
                {closeVerifyError && (
                  <Form.Text className="text-danger">{closeVerifyError}</Form.Text>
                )}
              </Form.Group>
              <div className="mt-3 border-top pt-3">
                <div className="text-muted fs-sm">ยอดคงเหลือปัจจุบัน</div>
                <div className="fw-bold">฿{fmt(account.current_balance)}</div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => { setShowCloseModal(false); setCloseStudentId(''); setCloseVerifyError('') }}>ยกเลิก</Button>
          <Button
            variant="danger"
            size="sm"
            disabled={closeLoading || closeStudentId.trim() !== String(account?.st_idnumber || '').trim()}
            onClick={handleCloseAccount}
          >
            {closeLoading ? <Spinner size="sm" className="me-1" /> : null}
            ยืนยันปิดบัญชี
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </>
  )
}
